import React, { useState, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { fetchRegionData, withAuthCheck, AuthError } from '../services/api';
import V3RegionDetails from './components/V3RegionDetails';
import CellArrivalAnimation from './components/CellArrivalAnimation';
import v3Theme from './theme';

const V3MapCell = ({ cell, chipFontSize = 10, bottomFontSize = 9, chipAlpha = 1.0, playerChipAlpha = 1.0, shouldAnimateArrival = false }) => {
    const token = useContext(AuthContext);
    const [openDialog, setOpenDialog] = useState(false);
    const [regionData, setRegionData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const isSpecial = cell.chipLabel === '📍' || cell.chipLabel === '🚩';
    const hasImage = cell.image && cell.image.length > 0;

    // Player position cells always use brand dark blue for highlighting
    // This distinguishes the player position from territory borders
    const highlightColor = v3Theme.main;

    
    // Helper function to apply alpha to hex color
    const applyAlphaToColor = (color, alpha) => {
        // If no color provided, return default with alpha
        if (!color) {
            return `rgba(156, 163, 175, ${alpha})`; // Default gray color with alpha
        }
        
        // If color is already rgba, update alpha
        if (color.startsWith('rgba')) {
            return color.replace(/[\d.]+\)$/, `${alpha})`);
        }
        
        // If color is rgb, convert to rgba
        if (color.startsWith('rgb')) {
            return color.replace('rgb', 'rgba').replace(')', `, ${alpha})`);
        }
        
        // If color is hex, convert to rgba
        if (color.startsWith('#')) {
            const r = parseInt(color.slice(1, 3), 16);
            const g = parseInt(color.slice(3, 5), 16);
            const b = parseInt(color.slice(5, 7), 16);
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        }
        
        // Return original color if format not recognized
        return color;
    };

    if (!cell.pointId) {
        // Empty cell
        return (
            <div style={{
                width: '100%',
                aspectRatio: '1',
                backgroundColor: v3Theme.secondary,
                border: `0.5px solid ${v3Theme.border.light}`,
                WebkitTapHighlightColor: 'transparent',
                tapHighlightColor: 'transparent',
            }} />
        );
    }

    const handleClick = (e) => {
        setOpenDialog(true);
        setIsLoading(true);
        setRegionData(null);

        withAuthCheck(fetchRegionData, token, cell.pointId)
            .then(data => {
                setRegionData(data);
                setIsLoading(false);
            })
            .catch(error => {
                if (error instanceof AuthError) {
                    setIsLoading(false);
                    return;
                }
                console.error('Error fetching region data:', error);
                setIsLoading(false);
            });
    };

    const handleClose = () => {
        setOpenDialog(false);
    };

    return (
        <>
            <CellArrivalAnimation trigger={shouldAnimateArrival && isSpecial}>
                <div
                    onClick={handleClick}
                    data-x={cell.x}
                    data-y={cell.y}
                    style={{
                        width: '100%',
                        aspectRatio: '1',
                        backgroundColor: v3Theme.secondary,
                        borderRadius: '8px',
                        border: (isSpecial && hasImage) ? `0.5px solid ${v3Theme.border.light}` : (isSpecial ? `4px solid ${highlightColor}` : `0.5px solid ${v3Theme.border.light}`),
                        boxSizing: 'border-box',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        overflow: hasImage ? 'visible' : 'hidden',
                        cursor: 'pointer',
                        transition: 'transform 0.2s ease-in-out',
                        position: 'relative',
                        WebkitTapHighlightColor: 'transparent',
                        tapHighlightColor: 'transparent',
                    }}
                >
                {/* Overlay border for special cells with images - renders on top without affecting layout */}
                {isSpecial && hasImage && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        borderLeft: `4px solid ${highlightColor}`,
                        borderRight: `4px solid ${highlightColor}`,
                        borderBottom: `4px solid ${highlightColor}`,
                        borderTop: 'none',
                        borderRadius: '8px',
                        pointerEvents: 'none',
                        zIndex: 10,
                    }} />
                )}
                {hasImage ? (
                    // Render image for special cell types
                    <>
                        <div style={{
                            width: '100%',
                            height: '100%',
                            position: 'relative',
                            overflow: 'visible', // Allow image to extend beyond cell
                            display: 'flex',
                            alignItems: 'flex-end', // Align to bottom
                            justifyContent: 'center',
                            pointerEvents: 'none', // Don't capture clicks on the image container
                        }}>
                            <img
                                src={`/img/map/${cell.image}.png`}
                                alt={cell.pointId}
                                style={{
                                    width: '100%',
                                    height: 'auto', // Maintain aspect ratio
                                    position: 'absolute',
                                    bottom: 0, // Align to bottom of cell
                                    left: '50%',
                                    transform: 'translateX(-50%)', // Center horizontally
                                    borderRadius: '8px', // Match the cell's border radius
                                    zIndex: 1,
                                    pointerEvents: 'none', // Image doesn't capture clicks
                                }}
                                onError={(e) => {
                                    // If image fails to load, hide it and show regular cell
                                    e.target.parentElement.style.display = 'none';
                                }}
                            />
                        </div>
                        {/* Show player pin overlay if player is on this cell */}
                        {isSpecial && (
                            <div style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                borderRadius: '50%',
                                width: '32px',
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '20px',
                                fontWeight: 'bold',
                                boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
                                border: `2px solid ${v3Theme.main}`,
                                zIndex: 2,
                                pointerEvents: 'none', // Overlay doesn't capture clicks
                            }}>
                                {cell.chipLabel}
                            </div>
                        )}
                    </>
                ) : (
                    // Regular cell content
                    <>
                        {/* Top zone with chip */}
                        <div
                                                        style={{
                                padding: '2px',
                                backgroundColor: applyAlphaToColor(
                                    cell.chipColor || v3Theme.text.disabled,
                                    chipAlpha
                                ),
                                height: '30%',
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                borderRadius: '4px',
                                userSelect: 'none',
                            }}
                        >
                            <span style={{
                                color: v3Theme.main,
                                fontSize: `${chipFontSize}px`,
                                fontWeight: 'bold',
                                fontFamily: 'JetBrains Mono, monospace',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                width: '100%',
                                textAlign: 'center',
                            }}>
                                {cell.chipLabel}
                            </span>
                        </div>

                        {/* Bottom zone */}
                        <div
                                                        style={{
                                height: '70%',
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                padding: '0 4px',
                                userSelect: 'none',
                                backgroundColor: 'transparent',
                                borderRadius: '0',
                            }}
                        >
                            <span style={{
                                color: '#7f8c8d',
                                fontSize: `${bottomFontSize}px`,
                                fontFamily: 'JetBrains Mono, monospace',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                width: '100%',
                                textAlign: 'center',
                            }}>
                                {cell.bottomLabel}
                            </span>
                        </div>
                    </>
                )}
                                </div>
            </CellArrivalAnimation>

            {openDialog && (
                <V3RegionDetails
                    open={openDialog}
                    onClose={handleClose}
                    title={regionData?.title}
                    subtitle={regionData?.subtitle}
                    icon={regionData?.icon}
                    extraInfo={regionData?.extraInfo}
                    routeInfo={regionData?.routeInfo}
                    steps={regionData?.steps}
                    commandValue={regionData?.commandValue}
                    canTravel={regionData?.canTravel}
                    isLoading={isLoading}
                    token={token}
                    pointId={cell.pointId}
                    lootstat={regionData?.lootstat}
                    canCaravan={regionData?.canCaravan}
                    caravanCost={regionData?.caravanCost}
                    caravanDuration={regionData?.caravanDuration}
                />
            )}
        </>
    );
};

export default React.memo(V3MapCell);